<template>
    <div style="width: 85vw; height: 80vh">
        <div>
            <div :id="'scichart-root-' + sid" :style="`width: 100%; height: ${sciChartComputedHeight}; margin-right: 10px; align-self: center;`"></div>
        </div>
        <div :style="`display: flex; flex-direction: column; flex: 1; height: calc(100% - ${sciChartComputedHeight});`">
            <ejs-grid ref="system_modal_grid" height="100%" width="100%" :allowTextWrap="true" :textWrapSettings="wrapSettings" :dataBound="dataBound" :dataSource="data" :allowPaging="true" :pageSettings="pageSettings" :allowFiltering="true" :filterSettings="filterMenu" :allowSorting="true" :showBusyIndicator="true" style="position: relative">
                <e-columns>
                    <e-column field="number" :headerText="$t('Unit')" textAlign="Left" :customAttributes="customAttributes"></e-column>
                    <e-column field="type" :headerText="$t('Type')" textAlign="Left" :customAttributes="customAttributes"></e-column>
                    <e-column field="alarmType" :headerText="$t('AlarmType')" textAlign="Left" :customAttributes="customAttributes"></e-column>
                    <e-column field="createDate" :headerText="$t('CreateDate')" textAlign="Left" :customAttributes="customAttributes" :filter="filterMenu" type="datetime" :format="dateTimeFormatOptions"></e-column>
                    <e-column field="endDate" :headerText="$t('EndDate')" textAlign="Left" :customAttributes="customAttributes" :filter="filterMenu" type="datetime" :format="dateTimeFormatOptions"></e-column>
                </e-columns>
            </ejs-grid>
        </div>
    </div>
</template>

<script>
import { SciChartSurface } from "scichart/Charting/Visuals/SciChartSurface";
import { EAxisAlignment } from "scichart/types/AxisAlignment";
import { FastLineRenderableSeries } from "scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries";
import { NumericAxis } from 'scichart/Charting/Visuals/Axis/NumericAxis';
import { NumberRange } from "scichart/Core/NumberRange";
import { SciChartJSLightTheme } from "scichart/Charting/Themes/SciChartJSLightTheme";

import { Sort, Page, Filter, Toolbar, Search, CommandColumn, PdfExport, ExcelExport } from "@syncfusion/ej2-vue-grids";
import { getDeviceAlarmFromDay } from '../../../api/api';
import { XyDataSeries } from 'scichart/Charting/Model/XyDataSeries';
import { ELineDrawMode } from 'scichart/Charting/Drawing/WebGlRenderContext2D';

import { ZoomPanModifier } from "scichart/Charting/ChartModifiers/ZoomPanModifier";
import { MouseWheelZoomModifier } from "scichart/Charting/ChartModifiers/MouseWheelZoomModifier";
import { RubberBandXyZoomModifier } from "scichart/Charting/ChartModifiers/RubberBandXyZoomModifier";
import { CursorModifier } from "scichart/Charting/ChartModifiers/CursorModifier";
import { ZoomExtentsModifier } from "scichart/Charting/ChartModifiers/ZoomExtentsModifier";

import moment from 'moment'
import { EXyDirection } from 'scichart/types/XyDirection';

export default {
    props: {
        sid: {
            type: String,
            default: 'none'
        },
        field: {
            type: Object,
            required: true
        }
    },
    computed: {
        sciChartComputedHeight() {
            return `calc(60px + ${40 * this.usedAlarmTypes}px)`
        }
    },
    components: {

    },
    provide: {
        grid: [ Sort, Page, Filter, Toolbar, Search, CommandColumn, PdfExport, ExcelExport ]
    },
    data() {
        return { 
            data: [],
            dateTimeFormatOptions: { format: 'yyyy-MM-dd HH:mm:ss', type:'dateTime' },
            customAttributes: { class: "customcss" },
            pageSettings: { pageSize: 100, pageSizes: [10, 25, 50, 100] },
            wrapSettings: { wrapMode: "Content" },
            filterMenu: {
                type: "Menu"
            },
            toolbar: {

            },
            sciChartSurface: null,
            wasmContext: null,
            xAxis: null,
            yAxis: null,
            usedAlarmTypes: 1
        }
    },
    methods: {
        async getData() {
            var yesterdaysData = await getDeviceAlarmFromDay(this.field.id, this.field.column.field, 1);
            var todaysData = await getDeviceAlarmFromDay(this.field.id, this.field.column.field, 0);

            this.data = yesterdaysData.concat(todaysData);

            this.initSciChart();
        },
        // CHART
        async initSciChart() {
            const {sciChartSurface, wasmContext} = await SciChartSurface.create("scichart-root-" + this.sid);

            this.sciChartSurface = sciChartSurface;
            this.wasmContext = wasmContext;

            this.sciChartSurface.applyTheme(new SciChartJSLightTheme())

            this.xAxis = new NumericAxis(this.wasmContext, {
                axisAlignment: EAxisAlignment.Top,
            });

            this.yAxis = new NumericAxis(this.wasmContext, {
                axisAlignment: EAxisAlignment.Left
            })

            this.yAxis.growBy = new NumberRange(0.02, 0.02);
            this.xAxis.growBy = new NumberRange(0.02, 0.02);

            this.xAxis.labelProvider.formatLabel = (dt) => {
                return moment(dt).format("DD.MM, HH:mm:ss")
            }

            this.sciChartSurface.xAxes.add(this.xAxis)
            this.sciChartSurface.yAxes.add(this.yAxis)

            this.populate()

            this.sciChartSurface.chartModifiers.add(new RubberBandXyZoomModifier({ xyDirection: EXyDirection.XDirection }));
            this.sciChartSurface.chartModifiers.add(new ZoomPanModifier({ xyDirection: EXyDirection.XDirection }));
            this.sciChartSurface.chartModifiers.add(new ZoomExtentsModifier({ xyDirection: EXyDirection.XDirection }));
            this.sciChartSurface.chartModifiers.add(new MouseWheelZoomModifier({ xyDirection: EXyDirection.XDirection }));

            this.sciChartSurface.chartModifiers.add(new CursorModifier({
                crosshairStroke: "#0095B2",
                crosshairStrokeThickness: 1,
                tooltipContainerBackground: "#000",
                tooltipTextStroke: "#0095B2",
                showTooltip: false,
                axisLabelFill: "#0095B2",
                axisLabelStroke: "#fff",
            }))
        },
        populate() {
            var alarmNames = [...new Set(this.data.map(x => x.alarmType))]

            let now = new Date();
            let yesterday = new Date();
            yesterday.setDate(now.getDate() - 1)
            yesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0)

            this.usedAlarmTypes = alarmNames.length;

            this.yAxis.labelProvider.formatLabel = (dataValue) => parseInt(dataValue);
            this.yAxis.maxAutoTicks = alarmNames.length;
            this.yAxis.minorsPerMajor = alarmNames.length
            this.yAxis.visibleRange = new NumberRange(-0.20, alarmNames.length - 0.80);
            this.yAxis.labelProvider.formatLabel = (val) => {
                return alarmNames[val]
            }

            alarmNames.forEach((alarmType, index) => {
                var filtered = this.data.filter(x => x.alarmType == alarmType).sort((a, b) => new Date(b.createDate) - new Date(a.createDate))

                const greenLineSeries = new FastLineRenderableSeries(this.wasmContext, {
                    dataSeries: new XyDataSeries(this.wasmContext, {
                        xValues: [yesterday, now],
                        yValues: [index, index]
                    }),
                    stroke: '#84CA85',
                    strokeThickness: 10
                })

                this.sciChartSurface.renderableSeries.add(greenLineSeries);

                var alarmDates = filtered.flatMap(x => [
                    new Date(x.createDate).getTime() - 1, 
                    new Date(x.createDate).getTime(), 
                    new Date(x.endDate) > new Date(now) ? now.getTime() : new Date(x.endDate).getTime(), 
                    new Date(x.endDate) > new Date(now) ? now.getTime() + 1 : new Date(x.endDate).getTime() + 1]
                )
                var alarmValues = filtered.flatMap(_ => [NaN, index, index, NaN])

                const redLineSeries = new FastLineRenderableSeries(this.wasmContext, {
                    dataSeries: new XyDataSeries(this.wasmContext, {
                        xValues: alarmDates,
                        yValues: alarmValues
                    }),
                    drawNaNAs: ELineDrawMode.DiscontinuousLine,
                    stroke: '#E06C65',
                    strokeThickness: 15
                })

                this.sciChartSurface.renderableSeries.add(redLineSeries);
            })

            this.sciChartSurface.zoomExtents();
        },
        // GRID
        dataBound: function() {
            this.$refs.system_modal_grid.autoFitColumns();
        },
    },
    mounted() {
        this.getData();
    }
}
</script>

<style>

</style>


// WEBPACK FOOTER //
// src/components/Systems/Devices/SciChartDeviceAlarmModal.vue